import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import { SavedAnswer, expertAgentApi, getExpertErrorMessage, isRequestCancelled } from '../../lib/domain-expert-api';
import AnswerEditor, { ANSWER_TOO_LONG, answerTooLong } from './AnswerEditor';
import { btnPrimary, btnSecondary, inputClass, labelClass } from './ui';

interface SavedAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  answer: SavedAnswer | null;
  readOnly: boolean;
}

export default function SavedAnswerModal({ isOpen, onClose, answer, readOnly }: SavedAnswerModalProps) {
  const queryClient = useQueryClient();
  const [question, setQuestion] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isEdit = !!answer;

  useEffect(() => {
    if (!isOpen) return;
    setQuestion(answer?.question ?? '');
    setAnswerText(answer?.answer ?? '');
    setError(null);
  }, [isOpen, answer]);

  const mutation = useMutation({
    mutationFn: async () => {
      const q = question.trim();
      const a = answerText.trim();
      if (answer) {
        const body: { question?: string; answer?: string } = {};
        if (q !== answer.question) body.question = q;
        if (a !== answer.answer) body.answer = a;
        if (Object.keys(body).length === 0) return answer;
        return expertAgentApi.updateAnswer(answer.id, body);
      }
      return expertAgentApi.createAnswer({ question: q, answer: a });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert', 'answers'] });
      toast.success('Answer saved');
      onClose();
    },
    onError: (err) => {
      if (isRequestCancelled(err)) return;
      setError(getExpertErrorMessage(err, "We couldn't save this answer. Please try again."));
    },
  });

  const handleSave = () => {
    if (!question.trim() || !answerText.trim()) {
      setError('Enter a question and an answer.');
      return;
    }
    if (answerTooLong(answerText)) {
      setError(ANSWER_TOO_LONG);
      return;
    }
    setError(null);
    mutation.mutate();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={mutation.isLoading ? () => undefined : onClose}
      title={isEdit ? 'Edit answer' : 'Add answer'}
      size="md"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={mutation.isLoading} className={`${btnSecondary} mt-3 sm:mt-0`}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={mutation.isLoading || readOnly} className={btnPrimary}>
            {mutation.isLoading ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          When a user asks a question like this one, your agent gives this exact answer.
        </p>
        <div>
          <label htmlFor="saved-question" className={labelClass}>
            Question
          </label>
          <input
            id="saved-question"
            type="text"
            value={question}
            maxLength={1000}
            disabled={readOnly}
            onChange={(e) => setQuestion(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <span className={labelClass}>Answer</span>
          <AnswerEditor value={answerText} onChange={setAnswerText} label="Answer" readOnly={readOnly} minHeight="15rem" />
        </div>
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
